import React, { useEffect, useState } from 'react';
import { Range } from 'react-range';
import { useQuery } from 'react-query';
import { evaluateAssetManagement } from 'config/api';
import { useScroll } from 'hooks/useScroll';
import { PremiumCheck2 as PremiumCheck } from 'components/commons/premium-check/PremiumCheck';
import Loading from 'components/commons/loading/Loading';
import {
  AccountId,
  TargetAccountItem,
  AssetTransitions,
  Target,
  howToSaveCash,
  howToSaveInvestmentProducts,
} from 'components/features/target-achievement-support/utils/type';
import { useTargetSupportRouting } from 'components/features/target-achievement-support/hooks/useTargetSupportRouting';
import { toNumber } from 'components/features/target-achievement-support/utils/utils';
import { useBirthday } from 'components/features/target-achievement-support/utils/birthday';
import { yieldFormatPercentage } from 'components/features/new-report/utils/formatter';
import {
  Mode,
  validateProps,
} from 'components/features/target-achievement-support/pages/TargetUpdateStep1';
import { isHowToSaveInvestmentTrust } from '../detail/TargetImformation';
import { NotFoundBirthday } from '../birthday/NotFoundBirthday';
import { Errors, Error } from '../../commons/Error';
import { SettingTargetChart } from '../detail/components/SettingTargetChart';
import { TargetPremium } from '../TargetPremium';

export type EvaluateAssetManagementProps = {
  assetTransitions: AssetTransitions[];
  yield: number;
  suggestionLimitPer: number | null;
  highRiskPer: number | null;
};

const clamp = (min: number, max: number, num: number): number => {
  return Math.max(min, Math.min(max, num));
};

const HowToSaveInformationCard = ({
  evaluateAssetManagement,
  reserveAmount,
  isLoading,
  isRefetching,
  isValid,
  setTargetYield,
  className,
}: {
  evaluateAssetManagement: EvaluateAssetManagementProps | null | undefined;
  reserveAmount: number | null;
  isLoading: boolean;
  isRefetching: boolean;
  isValid: boolean;
  setTargetYield?: (data: number | null) => void;
  className?: string;
}): React.ReactElement => {
  useEffect(() => {
    if (setTargetYield) {
      const targetYield_ =
        evaluateAssetManagement === undefined ||
        evaluateAssetManagement === null
          ? 0
          : evaluateAssetManagement.yield;
      setTargetYield(targetYield_);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluateAssetManagement]);

  const getHighRiskMessage = (
    annualYield: number,
    suggestionLimitPer: number,
    highRiskPer: number
  ): React.ReactElement => {
    if (annualYield < suggestionLimitPer / 100) return <></>;
    if (annualYield < highRiskPer / 100)
      return (
        <>
          「目的達成に必要な年利回り」が少し高めです。リスクを分散した運用の提案ができない場合があります
        </>
      );
    return (
      <>
        「目的達成に必要な年利回り」が高いです。
        <br />
        高いリターンが期待できる一方、リスクも高く、状況によっては達成できない可能性が大きくなります。また、リスク分散のための提案ができない場合があります。
      </>
    );
  };

  if (evaluateAssetManagement === undefined)
    return (
      <div
        className={`bg-gray-100 grid rounded-lg justify-center items-center ${className ??
          ''}`}
        style={{
          gridTemplateColumns: '1fr',
          height: '120px',
          placeItems: 'center',
        }}>
        <Loading loading circularLoading />
      </div>
    );

  if (isLoading || isRefetching)
    return (
      <div
        className={`bg-gray-100 grid rounded-lg justify-center items-center ${className ??
          ''}`}
        style={{
          gridTemplateColumns: '1fr',
          height: '120px',
          placeItems: 'center',
        }}>
        <Loading loading circularLoading />
      </div>
    );

  if (reserveAmount === null || evaluateAssetManagement === null || !isValid)
    return (
      <div
        className={`bg-gray-100 grid rounded-lg justify-center items-center ${className ??
          ''}`}
        style={{
          gridTemplateColumns: '1fr',
          height: '120px',
          placeItems: 'center',
        }}>
        つみたて額を見直して下さい
      </div>
    );

  return (
    <>
      {/* TODO: これ最後に対応するか判断する 入力中以外は対応済 */}
      {/* {(props.howToSave.isLoading ||
        props.howToSave.isReady ||
        props.howToSave.isReset ||
        props.howToSave.isFailed) && (
        <div
          className={`bg-gray-100 grid rounded-lg justify-center items-center ${props.className ??
            ''}`}
          style={{
            gridTemplateColumns: '1fr',
            height: '120px',
            placeItems: 'center',
          }}>
          {props.howToSave.isLoading && <Loading loading />}
          {props.howToSave.isReset && <>入力中...</>}
          {props.howToSave.isFailed && <>つみたて額を見直して下さい</>}
        </div>
      )} */}
      {evaluateAssetManagement &&
        ((): React.ReactElement => {
          const suggestionLimitPer =
            evaluateAssetManagement.suggestionLimitPer ?? 0;
          const highRiskPer = evaluateAssetManagement.highRiskPer ?? 0;
          const type =
            evaluateAssetManagement.yield === 0 ||
            evaluateAssetManagement.yield === null
              ? howToSaveCash
              : howToSaveInvestmentProducts;

          if (isHowToSaveInvestmentTrust(type)) {
            return (
              <>
                <div className={` ${className ?? ''}`}>
                  <div className={`rounded-lg bg-teal-500 text-white`}>
                    <div className="border-b border-white px-4 pt-4 pb-2 flex flex-row items-center">
                      <img
                        src="/static/images/advisor/investment1.svg"
                        alt="パソコン画像"
                        className="h-16 w-16 rounded-full"
                        style={{ objectFit: 'cover' }}
                      />
                      <div className="flex flex-col items-start ml-6">
                        <div className="text-sm">貯め方</div>
                        <div className="mt-1 text-lg font-bold">
                          投資商品で運用
                        </div>
                      </div>
                    </div>
                    <div className="px-4 py-2 flex flex-row items-center">
                      <div className="text-sm">目標達成に必要な年利回り</div>
                      <div className="font-bold text-lg ml-auto">
                        {yieldFormatPercentage(evaluateAssetManagement.yield, {
                          numberOfDecimals: 1,
                        })}
                        %
                      </div>
                    </div>
                  </div>
                  {evaluateAssetManagement.yield >=
                    suggestionLimitPer / 100 && (
                    <div className="flex flex-row items-start text-tradewell mx-2 mt-3">
                      <div className="icon-alert"></div>
                      <div className="text-sm ml-2">
                        {getHighRiskMessage(
                          evaluateAssetManagement.yield,
                          suggestionLimitPer,
                          highRiskPer
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            );
          } else {
            return (
              <>
                <div
                  className={`rounded-lg bg-blue-450 text-white flex flex-row px-4 py-4 ${className ??
                    ''}`}>
                  <img
                    src="/static/images/advisor/cash1.svg"
                    className="h-16 w-16 rounded-full"
                    alt="現金画像"
                    style={{ objectFit: 'cover' }}
                  />
                  <div className="ml-6 flex flex-col items-start">
                    <div className="text-sm">貯め方</div>
                    <div className="mt-1 text-lg font-bold">現金でつみたて</div>
                  </div>
                </div>
              </>
            );
          }
        })()}
    </>
  );
};

export const ChangeYieldCalculationForm = ({
  id,
  mode,
  target,
  rangeMax,
  selectedAccounts,
  reserveAmount,
  setReserveAmount,
  excludeAmount,
  setTargetYield,
  validateReserveAmount,
  setValidateReserveAmount,
}: {
  id: string;
  mode: Mode;
  target: Target;
  rangeMax: number;
  selectedAccounts: TargetAccountItem[];
  reserveAmount: number | null;
  setReserveAmount: (data: number | null) => void;
  excludeAmount: number;
  setTargetYield?: (data: number | null) => void;
  validateReserveAmount: validateProps;
  setValidateReserveAmount: (data: validateProps) => void;
}) => {
  const { goWhatIsNecessaryYield } = useTargetSupportRouting();

  const [isFirst, setIsFirst] = useState<boolean>(true);

  const rangeMin = 0;

  const selectedAccountIds: AccountId[] =
    selectedAccounts.length !== 0
      ? selectedAccounts.map(list => list.account)
      : [];

  const {
    data: evaluateAssetManagement,
    refetch,
    isLoading,
    isRefetching,
  } = useQuery(
    'targetHowToSavetestEvaluateAssetManagement',
    () =>
      evaluateAssetManagementAPI(
        target.id,
        selectedAccountIds,
        excludeAmount,
        reserveAmount,
        target.targetAmount,
        target.endDate
      ),
    {
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (isLoading || isRefetching) {
      setValidateReserveAmount({ isValid: false });
    } else {
      setValidateReserveAmount({ isValid: true });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isRefetching]);

  useEffect(() => {
    if (reserveAmount === null) {
      setValidateReserveAmount({
        isValid: false,
        error: '毎月のつみたて額を入力して下さい',
      });
    } else if (reserveAmount < 0) {
      setValidateReserveAmount({
        isValid: false,
        error: '毎月のつみたて額は0円以上入力してください',
      });
    } else {
      if (!isFirst) {
        (async () => {
          const timer = setTimeout(refetch, 1000);
          return () => clearTimeout(timer);
        })();
      } else {
        setIsFirst(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reserveAmount]);

  useScroll();

  const { birthday } = useBirthday();

  if (birthday === null || birthday === undefined)
    return (
      <>
        <PremiumCheck content={<TargetPremium />}>
          <NotFoundBirthday />
        </PremiumCheck>
      </>
    );

  return (
    <div className="p-4 mx-2 mt-8 bg-white flex flex-col rounded-lg shadow">
      <div className="flex flex-col">
        <div
          className="grid items-center"
          style={{ gridTemplateColumns: 'auto 1fr' }}>
          <div className="font-bold text-base">毎月のつみたて額</div>
          <div
            className="grid items-baseline ml-4"
            style={{ gridTemplateColumns: '1fr auto' }}>
            <input
              type="number"
              className="font-bold text-2xl border border-bluegray-300 rounded-lg py-1 px-4 text-right w-full"
              value={reserveAmount === null ? '' : reserveAmount}
              onChange={(e): void => {
                if (e.target.value !== '') {
                  const num = toNumber(e.target.value);
                  setReserveAmount(num);
                } else {
                  setReserveAmount(null);
                }
              }}
            />
            <div className="font-bold text-sm ml-2">円/月</div>
          </div>
        </div>
        {!validateReserveAmount.isValid && validateReserveAmount.error && (
          <Errors className="mx-4 mt-1">
            <Error
              error={{
                title: 'エラー',
                description: validateReserveAmount.error ?? '',
              }}
              errorToString={e => e.description}
            />
          </Errors>
        )}
        <Range
          {...{
            step: 1000,
            min: rangeMin,
            max: rangeMax,
            values:
              reserveAmount === null
                ? []
                : [clamp(rangeMin, rangeMax, reserveAmount)],
            onChange: (values): void => {
              (n => {
                setReserveAmount(n);
              })(values[0] ?? 0);
            },
            renderTrack: ({
              props: innerProps,
              children,
            }): React.ReactElement => {
              return (
                <div
                  {...innerProps}
                  className="my-8 mx-4 grid"
                  style={{
                    ...innerProps.style,
                    height: '4px',
                    gridTemplateColumns: ((percentage): string =>
                      `${percentage}fr ${100 - percentage}fr`)(
                      Math.floor(
                        ((clamp(
                          rangeMin,
                          rangeMax,
                          reserveAmount === null ? 0 : reserveAmount
                        ) -
                          rangeMin) /
                          (rangeMax - rangeMin)) *
                          100
                      )
                    ),
                  }}>
                  <div
                    className=""
                    style={{ backgroundColor: '#1d4ed8' }}></div>
                  <div
                    className=""
                    style={{ backgroundColor: '#dee5ee' }}></div>
                  {children}
                </div>
              );
            },
            renderThumb: ({ props }): React.ReactElement => {
              return (
                <div
                  {...props}
                  style={{
                    ...props.style,
                    height: '24px',
                    width: '24px',
                    borderRadius: '50%',
                    backgroundColor: '#f1f5f9',
                    border: 'solid 2px #64748b',
                  }}
                />
              );
            },
          }}
        />
      </div>
      <div className="border-b" style={{ borderColor: '#E2E8F0' }}></div>
      {mode === 'edit' &&
      !isLoading &&
      !isRefetching &&
      reserveAmount !== null &&
      evaluateAssetManagement &&
      evaluateAssetManagement.assetTransitions ? (
        <SettingTargetChart
          birthday={birthday}
          targetAmount={target.targetAmount}
          assetTransitions={evaluateAssetManagement.assetTransitions}
        />
      ) : (
        <></>
      )}
      <HowToSaveInformationCard
        {...{
          evaluateAssetManagement,
          reserveAmount,
          isLoading,
          isRefetching,
          isValid: validateReserveAmount.isValid,
          setTargetYield,
        }}
        className="mt-4"
      />
      <div
        className="flex flex-row ml-auto items-center mt-2 cursor-pointer"
        onClick={() => goWhatIsNecessaryYield(id)}>
        <div className="text-sm text-bluegray-500">必要な年利回りとは</div>
        <div className="icon-help_outline text-base text-bluegray-400 ml-2"></div>
      </div>
    </div>
  );
};

const evaluateAssetManagementAPI = async (
  id: string,
  accountIDList: string[],
  excludeAmount: number | null,
  reserveAmount: number | null,
  targetAmount: number,
  endDate: string
): Promise<EvaluateAssetManagementProps | null> => {
  if (reserveAmount === null || reserveAmount < 0) return null;

  const validExcludeAmount = excludeAmount ?? 0;
  const res_ = await evaluateAssetManagement(
    id,
    accountIDList,
    validExcludeAmount,
    reserveAmount,
    targetAmount,
    endDate
  );

  if (res_ === null) return null;
  const res = {
    yield: res_.Yield !== null ? res_.Yield : 0,
    suggestionLimitPer: res_.SuggestionLimitPer,
    highRiskPer: res_.HighRiskPer,
    assetTransitions: res_.AssetTransitions.map(item => {
      return {
        dateYM: item.DateYM,
        assetAmount: item.AssetAmount,
      };
    }),
  };

  return res as EvaluateAssetManagementProps;
};
